import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
export function BillApprovalWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [approvalDecision, setApprovalDecision] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [verificationChecks, setVerificationChecks] = useState({
    supplierVerified: false,
    amountVerified: false,
    itemsVerified: false,
    termsVerified: false
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-001',
    supplierName: 'Premium Tea Estates Ltd',
    amount: 125000,
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [{
      id: '1',
      description: 'Ceylon Black Tea - Grade A',
      quantity: 500,
      unitPrice: 200,
      total: 100000
    }, {
      id: '2',
      description: 'Packaging & Handling',
      quantity: 1,
      unitPrice: 25000,
      total: 25000
    }]
  };
  const handleCheckboxChange = (key: keyof typeof verificationChecks) => {
    setVerificationChecks({
      ...verificationChecks,
      [key]: !verificationChecks[key]
    });
  };
  const allChecksCompleted = Object.values(verificationChecks).every(check => check);
  const handleSubmit = () => {
    console.log('Submitting approval:', {
      billId: id,
      decision: approvalDecision,
      notes,
      verificationChecks
    });
    navigate('/bills');
  };
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-2474">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2475">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-2476">
          <ArrowLeftIcon size={20} data-id="element-2477" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2478">
        <div className="flex items-center justify-between mb-8" data-id="element-2479">
          <div className="flex-1" data-id="element-2480">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2481">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2482">
                1
              </div>
              <div className="ml-3" data-id="element-2483">
                <div className="text-sm font-medium" data-id="element-2484">Review Bill</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2485"></div>
          <div className="flex-1" data-id="element-2486">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2487">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2488">
                2
              </div>
              <div className="ml-3" data-id="element-2489">
                <div className="text-sm font-medium" data-id="element-2490">Verification</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2491"></div>
          <div className="flex-1" data-id="element-2492">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2493">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2494">
                3
              </div>
              <div className="ml-3" data-id="element-2495">
                <div className="text-sm font-medium" data-id="element-2496">Decision</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Review Bill */}
        {step === 1 && <div className="space-y-6" data-id="element-2497">
            <div data-id="element-2498">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2499">
                Review Bill Details
              </h2>
              <p className="text-gray-600" data-id="element-2500">
                Please review the bill information carefully before proceeding
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-2501">
              <div className="grid grid-cols-2 gap-4" data-id="element-2502">
                <div data-id="element-2503">
                  <div className="text-sm text-gray-500" data-id="element-2504">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2505">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-2506">
                  <div className="text-sm text-gray-500" data-id="element-2507">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2508">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-2509">
                  <div className="text-sm text-gray-500" data-id="element-2510">Issue Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2511">
                    {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-2512">
                  <div className="text-sm text-gray-500" data-id="element-2513">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2514">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
              <div data-id="element-2515">
                <div className="text-sm text-gray-500 mb-2" data-id="element-2516">Description</div>
                <div className="text-base text-gray-900" data-id="element-2517">
                  {bill.description}
                </div>
              </div>
              <div data-id="element-2518">
                <div className="text-sm text-gray-500 mb-3" data-id="element-2519">Items</div>
                <div className="space-y-2" data-id="element-2520">
                  {bill.items.map(item => <div key={item.id} className="flex justify-between items-center p-3 bg-white rounded border border-gray-200" data-id="element-2521">
                      <div data-id="element-2522">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2523">
                          {item.description}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2524">
                          {item.quantity} × Rs.{' '}
                          {item.unitPrice.toLocaleString('en-LK')}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-2525">
                        Rs. {item.total.toLocaleString('en-LK')}
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-2526">
                <div className="flex justify-between items-center" data-id="element-2527">
                  <span className="text-lg font-semibold text-gray-900" data-id="element-2528">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-gray-900" data-id="element-2529">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-2530">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-2531">
                Continue to Verification
              </button>
            </div>
          </div>}
        {/* Step 2: Verification */}
        {step === 2 && <div className="space-y-6" data-id="element-2532">
            <div data-id="element-2533">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2534">
                Verification Checklist
              </h2>
              <p className="text-gray-600" data-id="element-2535">
                Please verify the following items before making a decision
              </p>
            </div>
            <div className="space-y-4" data-id="element-2536">
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2537">
                <input type="checkbox" checked={verificationChecks.supplierVerified} onChange={() => handleCheckboxChange('supplierVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2538" />
                <div className="ml-3" data-id="element-2539">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2540">
                    Supplier Information Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2541">
                    Confirm that the supplier details are correct and the
                    supplier is authorized
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2542">
                <input type="checkbox" checked={verificationChecks.amountVerified} onChange={() => handleCheckboxChange('amountVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2543" />
                <div className="ml-3" data-id="element-2544">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2545">
                    Amount Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2546">
                    Confirm that the total amount matches the purchase order or
                    agreement
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2547">
                <input type="checkbox" checked={verificationChecks.itemsVerified} onChange={() => handleCheckboxChange('itemsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2548" />
                <div className="ml-3" data-id="element-2549">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2550">
                    Items and Quantities Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2551">
                    Confirm that all items and quantities are correct
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2552">
                <input type="checkbox" checked={verificationChecks.termsVerified} onChange={() => handleCheckboxChange('termsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2553" />
                <div className="ml-3" data-id="element-2554">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2555">
                    Payment Terms Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2556">
                    Confirm that the payment terms and due date are acceptable
                  </div>
                </div>
              </label>
            </div>
            {!allChecksCompleted && <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-2557">
                <AlertCircleIcon size={20} className="text-yellow-600 mt-0.5" data-id="element-2558" />
                <div className="text-sm text-yellow-800" data-id="element-2559">
                  Please complete all verification checks before proceeding
                </div>
              </div>}
            <div className="flex justify-between" data-id="element-2560">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2561">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!allChecksCompleted} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2562">
                Continue to Decision
              </button>
            </div>
          </div>}
        {/* Step 3: Decision */}
        {step === 3 && <div className="space-y-6" data-id="element-2563">
            <div data-id="element-2564">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2565">
                Approval Decision
              </h2>
              <p className="text-gray-600" data-id="element-2566">
                Make your final decision on this bill
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2567">
              <button onClick={() => setApprovalDecision('approve')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'approve' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`} data-id="element-2568">
                <CheckCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'approve' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-2569" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-2570">
                  Approve
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-2571">
                  Approve this bill for payment
                </div>
              </button>
              <button onClick={() => setApprovalDecision('reject')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'reject' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}`} data-id="element-2572">
                <XCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'reject' ? 'text-red-600' : 'text-gray-400'}`} data-id="element-2573" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-2574">
                  Reject
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-2575">
                  Reject this bill and return to supplier
                </div>
              </button>
            </div>
            <div data-id="element-2576">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2577">
                Notes {approvalDecision === 'reject' && '*'}
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder={approvalDecision === 'reject' ? 'Please provide a reason for rejection...' : 'Add any additional notes (optional)...'} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2578" />
            </div>
            <div className="flex justify-between" data-id="element-2579">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2580">
                Back
              </button>
              <button onClick={handleSubmit} disabled={!approvalDecision || approvalDecision === 'reject' && !notes} className={`px-6 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed ${approvalDecision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-2581">
                {approvalDecision === 'approve' ? 'Approve Bill' : 'Reject Bill'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}